/**
 * Utility functions for formatting currency amounts
 * Using TZS (ISO 4217 code for Tanzanian Shilling)
 */

/**
 * Format amount as Tanzanian Shillings
 * @param amount - The amount to format
 * @param showDecimals - Whether to show decimal places (default: false)
 * @returns Formatted currency string
 */
export function formatTZS(amount: number, showDecimals: boolean = false): string {
  if (isNaN(amount)) return 'TZS 0';

  const formatted = showDecimals
    ? amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : amount.toLocaleString('en-US', { maximumFractionDigits: 0 });

  return `TZS ${formatted}`;
}

/**
 * @deprecated Use formatTZS instead
 */
export const formatTSh = formatTZS;

/**
 * Format amount with unit suffix for large numbers
 * @param amount - The amount to format
 * @param currency - Currency symbol (default: 'TZS')
 * @returns Formatted currency string with K/M suffix
 */
export function formatTZSCompact(amount: number, currency: string = 'TZS'): string {
  if (isNaN(amount)) return `${currency} 0`;

  if (amount >= 1000000) {
    return `${currency} ${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${currency} ${(amount / 1000).toFixed(1)}K`;
  }

  return `${currency} ${amount.toLocaleString()}`;
}

/**
 * @deprecated Use formatTZSCompact instead
 */
export const formatTShCompact = formatTZSCompact;

/**
 * Parse currency string to number
 * @param currencyString - Currency string like "TZS 1,500" or "1,500"
 * @returns Parsed number
 */
export function parseTZS(currencyString: string): number {
  return Number(currencyString.replace(/[^0-9.-]/g, ''));
}

/**
 * @deprecated Use parseTZS instead
 */
export const parseTSh = parseTZS;
